'use client';

import { twMerge } from '@shared/themes';
import { useEffect, useState, type CSSProperties } from 'react';
import { ReplayDrawButton } from './ReplayDrawButton';

type ReplayDrawSlotProps = Readonly<{
  canStartReplay: boolean;
  onReplay: () => void;
}>;

/** Ease-open length. The overflow clip releases after this, and the transition reads it as `--replay-slot-duration`. */
const REPLAY_SLOT_TRANSITION_MS = 300;

/**
 * Holds the replay control's space under the live-draw stage and eases it open (WEBD-1528).
 *
 * Mounting the button only once a live draw finishes shoves everything under the panel. Keeping it
 * in the flow, collapsed, lets that space grow instead of appearing at full height.
 */
function ReplayDrawSlot({ canStartReplay, onReplay }: ReplayDrawSlotProps) {
  // Overflow clips while the row is moving, then releases so the focus ring is not cut off. Closing
  // resets it during render; opening waits out the transition on a timer rather than `transitionend`,
  // which never fires under reduced motion or where grid rows do not interpolate.
  const [settledOpen, setSettledOpen] = useState(canStartReplay);
  const [available, setAvailable] = useState(canStartReplay);

  if (canStartReplay !== available) {
    setAvailable(canStartReplay);
    if (!canStartReplay) {
      setSettledOpen(false);
    }
  }

  useEffect(() => {
    if (!canStartReplay || settledOpen) {
      return;
    }

    const timeoutId = window.setTimeout(() => setSettledOpen(true), REPLAY_SLOT_TRANSITION_MS);
    return () => window.clearTimeout(timeoutId);
  }, [canStartReplay, settledOpen]);

  return (
    <div
      data-testid="replay-draw-slot"
      style={{ '--replay-slot-duration': `${REPLAY_SLOT_TRANSITION_MS}ms` } as CSSProperties}
      className={twMerge(
        'grid motion-safe:transition-[grid-template-rows,opacity] motion-safe:duration-(--replay-slot-duration) motion-safe:ease-out',
        canStartReplay ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
      )}
      inert={!canStartReplay}
      aria-hidden={canStartReplay ? undefined : true}
    >
      <div className={twMerge('min-h-0', !settledOpen && 'overflow-hidden')}>
        <div className="pt-4">
          <ReplayDrawButton onClick={onReplay} />
        </div>
      </div>
    </div>
  );
}

export { REPLAY_SLOT_TRANSITION_MS, ReplayDrawSlot };
export type { ReplayDrawSlotProps };
